import Link from 'next/link';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { PopImage } from '@ps/ui/atoms/pop-image';
import { cn } from '@ps/ui/utils';
import { UserRound } from 'lucide-react';

import { dmSubscribeHref } from '../functions/dmSubscribeHref';

interface Props {
  bioContext: BioContext;
  creatorName: string;
  creatorAvatar?: string;
  /** The creator's `store.dmMinimumSubscriptionLevel` — floors + preselects the
   *  tier in the subscribe picker the CTA opens. */
  dmMinimumSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
  className?: string;
}

const AVATAR_SIZE = 72;

/**
 * The "not subscribed" DM upsell card — the web port of PopshopRN's `ModalContent`
 * (ChatButton). Avatar + "You're not subscribed!" + a "real creators, not bots"
 * callout + a "Subscribe and Chat Now" CTA that opens the real subscribe picker
 * floored/preselected at the creator's `dmMinimumSubscriptionLevel`
 * ({@link dmSubscribeHref}). Rendered inline by the `/chat` destination gate and
 * inside the {@link NotSubscribedUpsell} dialog opened from the Message Me button.
 */
export function SubscribeGateView({
  bioContext,
  creatorName,
  creatorAvatar,
  dmMinimumSubscriptionLevel,
  className,
}: Props) {
  return (
    <div
      className={cn(
        'flex w-full max-w-[360px] flex-col items-center gap-4 text-center text-white',
        className,
      )}
    >
      <div className="h-[72px] w-[72px] overflow-hidden rounded-full ring-2 ring-white/20">
        {creatorAvatar ? (
          <PopImage
            src={creatorAvatar}
            alt={creatorName}
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            resizing={{ width: 144, height: 144, fit: 'cover' }}
            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-white/10">
            <UserRound className="h-8 w-8 text-white/70" aria-hidden />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-white/60">Uh-oh!</p>
        <p className="text-lg font-bold">You&apos;re not subscribed!</p>
        <p className="text-sm text-white/70">
          You&apos;ll need to subscribe to {creatorName} to get their attention
          and chat with them.
        </p>
      </div>

      <div className="flex flex-col gap-1 rounded-xl bg-white/5 p-4">
        <p className="text-sm font-semibold text-white">
          Chat with Real Creators — Not Bots!
        </p>
        <p className="text-sm text-white/70">
          Unlock direct access to the creator! Get real responses from real
          people — no AI, no scripts.
        </p>
      </div>

      <Link
        href={dmSubscribeHref(bioContext, dmMinimumSubscriptionLevel)}
        className="w-full max-w-[300px] rounded-full bg-white px-4 py-3 text-sm font-semibold text-black transition hover:bg-white/90"
      >
        Subscribe and Chat Now
      </Link>
    </div>
  );
}
